import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, FileText, Gavel, ListTodo, Loader2, SearchX } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { useDebounce } from '@/lib/hooks';
import { useAuth } from '@/features/auth';
import { contractsApi } from '@/features/contracts/api/contracts.api';
import { mattersApi } from '@/features/matters/api/matters.api';
import { newTasksApi } from '@/features/new-tasks/api/tasks.api';

const MIN_CHARS = 2;
const RESULT_LIMIT = 5;

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** A single result row — tinted icon chip · title + subtitle · id badge. */
function ResultRow({
  value,
  icon,
  accent,
  title,
  subtitle,
  id,
  onSelect,
}: {
  value: string;
  icon: React.ReactNode;
  accent: string;
  title: string;
  subtitle?: string | undefined;
  id: number | undefined;
  onSelect: () => void;
}) {
  return (
    <CommandItem
      value={value}
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-3 rounded-lg px-2.5 py-2"
    >
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg [&_svg]:h-[18px] [&_svg]:w-[18px]',
          accent
        )}
      >
        {icon}
      </span>
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium text-foreground">{title}</span>
        {subtitle && (
          <span className="truncate text-xs text-muted-foreground">{subtitle}</span>
        )}
      </div>
      <span className="ml-auto shrink-0 rounded-md bg-muted px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-muted-foreground">
        #{id}
      </span>
    </CommandItem>
  );
}

/**
 * Dashboard global search — one modal that searches Contracts, Cases (matters)
 * and Tasks at once (server-side `s` query, debounced) and deep-links each hit
 * to its detail page. Each group is gated by the matching `:get` permission so
 * we never fire a query the user can't read.
 */
export function GlobalSearchDialog({ open, onOpenChange }: GlobalSearchDialogProps) {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { hasAccess } = useAuth();

  const [query, setQuery] = React.useState('');
  const debounced = useDebounce(query.trim());
  const active = debounced.length >= MIN_CHARS;

  const canSearchContracts = hasAccess('contract', 'get');
  const canSearchMatters = hasAccess('matter', 'get');
  const canSearchTasks = hasAccess('task', 'get');

  // Reset the query whenever the dialog closes so it reopens clean.
  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const contractsQuery = useQuery({
    queryKey: ['global-search', 'contracts', debounced],
    queryFn: () => contractsApi.getAll({ s: debounced, limit: RESULT_LIMIT }),
    enabled: open && active && canSearchContracts,
    staleTime: 30_000,
  });

  const mattersQuery = useQuery({
    queryKey: ['global-search', 'matters', debounced],
    queryFn: () => mattersApi.getMatters({ s: debounced, limit: RESULT_LIMIT }),
    enabled: open && active && canSearchMatters,
    staleTime: 30_000,
  });

  const tasksQuery = useQuery({
    queryKey: ['global-search', 'tasks', debounced],
    queryFn: () => newTasksApi.getAll({ s: debounced, limit: RESULT_LIMIT, page: 0 }),
    enabled: open && active && canSearchTasks,
    staleTime: 30_000,
  });

  const contracts = active ? (contractsQuery.data?.results ?? []) : [];
  const matters = active ? (mattersQuery.data?.results ?? []) : [];
  const tasks = active ? (tasksQuery.data?.results ?? []) : [];

  const isFetching =
    contractsQuery.isFetching || mattersQuery.isFetching || tasksQuery.isFetching;
  const hasResults = contracts.length > 0 || matters.length > 0 || tasks.length > 0;
  const showEmpty = active && !isFetching && !hasResults;

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  const untitled = t('dashboard.case360.search.untitled');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="overflow-hidden p-0 sm:max-w-xl"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>{t('dashboard.case360.search.title')}</DialogTitle>
        </DialogHeader>
        <Command
          shouldFilter={false}
          className="[&_[cmdk-input-wrapper]]:px-4 [&_[cmdk-input-wrapper]]:h-14 [&_[cmdk-input-wrapper]_svg]:h-[18px] [&_[cmdk-input-wrapper]_svg]:w-[18px] [&_[cmdk-input]]:text-[15px] [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1.5 [&_[cmdk-group-heading]]:pt-1 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground"
        >
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={t('dashboard.case360.search.placeholder')}
          />
          <CommandList className="max-h-[62vh] min-h-[260px] px-2 py-2">
            {!active && (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <SearchX className="h-5 w-5" />
                </span>
                <p className="text-sm text-muted-foreground">
                  {t('dashboard.case360.search.hint')}
                </p>
              </div>
            )}

            {active && isFetching && !hasResults && (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin" />
                <p className="text-sm">{t('dashboard.case360.search.searching')}</p>
              </div>
            )}

            {showEmpty && (
              <CommandEmpty className="px-6 py-14">
                <div className="flex flex-col items-center justify-center gap-3 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
                    <SearchX className="h-5 w-5" />
                  </span>
                  <p className="text-sm text-muted-foreground">
                    {t('dashboard.case360.search.noResults')}
                  </p>
                </div>
              </CommandEmpty>
            )}

            {matters.length > 0 && (
              <CommandGroup heading={t('dashboard.case360.search.groupCases')}>
                {matters.map((m) => (
                  <ResultRow
                    key={`matter-${m?.id}`}
                    value={`matter-${m?.id}`}
                    icon={m?.isCourtCase ? <Gavel /> : <Briefcase />}
                    accent="bg-indigo-50 text-indigo-600"
                    title={m?.name || m?.caseNumber || untitled}
                    subtitle={m?.client?.name}
                    id={m?.id}
                    onSelect={() => go(`/app/matters/${m?.id}`)}
                  />
                ))}
              </CommandGroup>
            )}

            {contracts.length > 0 && (
              <CommandGroup heading={t('dashboard.case360.search.groupContracts')}>
                {contracts.map((c) => (
                  <ResultRow
                    key={`contract-${c?.id}`}
                    value={`contract-${c?.id}`}
                    icon={<FileText />}
                    accent="bg-emerald-50 text-emerald-600"
                    title={c?.name || untitled}
                    subtitle={c?.client?.name}
                    id={c?.id}
                    onSelect={() => go(`/app/contracts/${c?.id}`)}
                  />
                ))}
              </CommandGroup>
            )}

            {tasks.length > 0 && (
              <CommandGroup heading={t('dashboard.case360.search.groupTasks')}>
                {tasks.map((tk) => (
                  <ResultRow
                    key={`task-${tk?.id}`}
                    value={`task-${tk?.id}`}
                    icon={<ListTodo />}
                    accent="bg-amber-50 text-amber-600"
                    title={tk?.title || untitled}
                    subtitle={tk?.matter?.name}
                    id={tk?.id}
                    onSelect={() => go(`/app/new-tasks/${tk?.id}`)}
                  />
                ))}
              </CommandGroup>
            )}
          </CommandList>

          <div className="flex items-center gap-4 border-t bg-muted/30 px-4 py-2.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <kbd className="rounded border bg-background px-1.5 py-0.5 font-sans text-[10px] shadow-sm">
                ↑↓
              </kbd>
              {t('dashboard.case360.search.kbdNavigate')}
            </span>
            <span className="flex items-center gap-1.5">
              <kbd className="rounded border bg-background px-1.5 py-0.5 font-sans text-[10px] shadow-sm">
                ↵
              </kbd>
              {t('dashboard.case360.search.kbdOpen')}
            </span>
            <span className="ml-auto flex items-center gap-1.5">
              <kbd className="rounded border bg-background px-1.5 py-0.5 font-sans text-[10px] shadow-sm">
                Esc
              </kbd>
              {t('dashboard.case360.search.kbdClose')}
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
